<template>
  <div :class="['app-download-cta', `app-download-cta--${variant}`]">
    <div v-if="variant === 'section' && showHeading"
class="app-download-cta__heading">
      <h2 class="app-download-cta__title">Bzioup lives in the app.</h2>
      <p class="app-download-cta__subtitle">
        Download it now. the experience is built for your phone.
      </p>
    </div>

    <div class="app-download-cta__buttons">
      <a
        class="app-download-cta__btn app-download-cta__btn--ios"
        :href="iosStoreUrl"
        rel="noopener"
      >
        <span class="app-download-cta__btn-label">
          <span class="app-download-cta__btn-tagline">Download on the</span>
          <span class="app-download-cta__btn-store">App Store</span>
        </span>
      </a>
      <a
        class="app-download-cta__btn app-download-cta__btn--android"
        :href="androidStoreUrl"
        rel="noopener"
      >
        <span class="app-download-cta__btn-label">
          <span class="app-download-cta__btn-tagline">Get it on</span>
          <span class="app-download-cta__btn-store">Google Play</span>
        </span>
      </a>
    </div>

    <p v-if="showBetaNote"
class="app-download-cta__beta-note">
      Bzioup is in beta. If the links above don't open a store yet,
      the app isn't publicly available yet. Drop us a note via the
      <router-link to="/contact">contact page</router-link>
      if you'd like an invite.
    </p>
  </div>
</template>

<script>
/**
 * Reusable "Get the app" CTA: two store buttons + optional heading
 * + optional beta-honest note.
 *
 * Used by:
 *   - hello/src/views/UserProfile.vue (variant="card", no heading,
 *     beta-note=true). landing page for shared profile links when
 *     the OS didn't intercept the URL as a Universal/App Link.
 *   - hello/src/views/Home.vue (variant="section", heading=true,
 *     beta-note=true). gives the marketing home a real install
 *     path. Without this, share-app links from the mobile app land
 *     on a beautiful but actionless page.
 *
 * Store URLs are placeholders until the production apps ship.
 * Mirror the same placeholders in aldo/src/config/settings.ts
 * (used by the in-app force-update modal's "Update" CTA).
 */
export default {
  name: 'AppDownloadCTA',
  props: {
    /** 'card'    → tight padding, inside a card (UserProfile page)
     *  'section' → full-width section with optional heading (Home) */
    variant: {
      type: String,
      default: 'card',
      validator: (v) => ['card', 'section'].includes(v),
    },
    showHeading: { type: Boolean, default: true },
    showBetaNote: { type: Boolean, default: true },
  },
  computed: {
    iosStoreUrl() {
      return 'https://apps.apple.com/app/bzioup/id6758737021'
    },
    androidStoreUrl() {
      return 'https://play.google.com/store/apps/details?id=com.bzioup.app'
    },
  },
}
</script>

<style lang="scss" scoped>
.app-download-cta {
  text-align: center;
}

/* Section variant: full-width band with breathing room. Used on the
   home page between Testimonials and RotatingWords. */
.app-download-cta--section {
  padding: 64px 24px;
  background: var(--bzioup-bg-color-Z, #f7f6f3);
}

/* Card variant: tighter spacing, expects to live inside a card with
   its own padding (UserProfile.vue). */
.app-download-cta--card {
  padding: 0;
}

.app-download-cta__heading {
  max-width: 560px;
  margin: 0 auto 28px;
}

.app-download-cta__title {
  margin: 0 0 8px;
  font-size: 28px;
  font-weight: 600;
  line-height: 1.25;
}

.app-download-cta__subtitle {
  margin: 0;
  font-size: 15px;
  color: var(--bzioup-text-color-soft, #777);
  line-height: 1.5;
}

.app-download-cta__buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 420px;
  margin: 0 auto;
}

.app-download-cta__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 18px;
  border-radius: 10px;
  text-decoration: none;
  background: #000;
  color: #fff;
  font-size: 14px;
  transition: opacity 150ms ease;

  &:hover { opacity: 0.85; }
}

.app-download-cta__btn-label {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.app-download-cta__btn-tagline {
  font-size: 10px;
  letter-spacing: 0.4px;
  opacity: 0.8;
}

.app-download-cta__btn-store {
  font-size: 16px;
  font-weight: 600;
}

.app-download-cta__beta-note {
  margin: 18px auto 0;
  max-width: 480px;
  font-size: 12px;
  line-height: 1.55;
  color: var(--bzioup-text-color-soft, #888);
  font-style: italic;

  a {
    color: var(--bzioup-color-pink);
    text-decoration: underline;
  }
}

/* Side-by-side buttons above ~480px viewport. Below, stacked is
   more thumb-friendly. */
@media (min-width: 480px) {
  .app-download-cta__buttons {
    flex-direction: row;
  }
  .app-download-cta__btn {
    flex: 1;
  }
}
</style>
